import React, { useEffect, useRef, useState } from "react";
import { EmblaOptionsType } from "embla-carousel";
import useEmblaCarousel from "embla-carousel-react";
import Link from "next/link";
import { ExpandMore24, PlayButtonWhiteBackground } from "@/assets/Icons";
import { PrevButton, NextButton, usePrevNextButtons } from "./CarouselPrevNextButton";
import { DotButton, useDotButton } from "./CarouselDotButtons";
import AppText from "../other/AppText";
import Image from "next/image";
import styles from "./style.module.css";
import VideoModal from "./videoModal";

export interface ISlidesProps {
  id: string;
  heading: string;
  excerpt: string;
  imgUrl: string;
  readMoreUrl: string;
  featuredImage: { src: string }[];
  video?: { videoPreviewURLs: string[] }[];
  youtubeVideoId?: string;
}

interface ICarouselProps {
  slides: ISlidesProps[];
  options?: EmblaOptionsType;
  heading?: string;
  readMore?: string;
}

const extractYouTubeId = (url: string): string | undefined => {
  const regex = /(?:youtube\.com\/(?:[^/\n\s]+\/\S+\/|(?:v|embed|shorts)\/|.*[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
  const match = url.match(regex);
  return match?.[1] || undefined;
};

const EmblaItemsCarousel: React.FC<ICarouselProps> = ({ slides, options, heading, readMore }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel(options);
  const { selectedIndex, onDotButtonClick } = useDotButton(emblaApi);
  const { prevBtnDisabled, nextBtnDisabled, onPrevButtonClick, onNextButtonClick } = usePrevNextButtons(emblaApi);
  const excerptRefs = useRef<Set<HTMLDivElement>>(new Set());
  const [excerptMinHeight, setExcerptMinHeight] = useState<number>();
  const [modalData, setModalData] = useState<{
    videoSrc?: string;
    youtubeId?: string;
    isYouTube: boolean;
  } | null>(null);

  const addToExcerptRefs = (el: HTMLDivElement | null) => {
    if (el) excerptRefs.current.add(el);
  };

  useEffect(() => {
    const heights = Array.from(excerptRefs.current).map((el) => el.offsetHeight || 0);
    const maxHeight = Math.max(...heights);
    setExcerptMinHeight(maxHeight);
  }, [slides]);

  return (
    <section className={styles.carousel_section}>
      <div className="flex flex-row w-full">
        <div className="basis-2/3">
          <AppText type="HEADLINE_MEDIUM" className="mb-1">
            <div className={`font-sairaSemiCondensed text-left ${styles.title_heading}`}>{heading}</div>
          </AppText>
        </div>
        <div className="basis-1/3 alignItems-center text-right">
          <div className={styles.carousel_buttons}>
            <PrevButton onClick={onPrevButtonClick} disabled={prevBtnDisabled} />
            <NextButton onClick={onNextButtonClick} disabled={nextBtnDisabled} />
          </div>
        </div>
      </div>

      <div className={styles.carousel_viewport} ref={emblaRef}>
        <div className={styles.carousel_container}>
          {slides?.map((item) => {
            const ytId = item.youtubeVideoId ? extractYouTubeId(item.youtubeVideoId) : undefined;
            return (
              <div className={styles.carousel_slide} key={item.id}>
                <div className={styles.carousel_slide_item}>
                  <div className={styles.image_item}>
                    {item.video?.[0]?.videoPreviewURLs?.[0] ? (
                      <>
                        <Image
                          src={item.featuredImage?.[0]?.src || "/default-thumbnail.jpg"}
                          width={302}
                          height={182}
                          alt={item.heading}
                          className={styles.thumbnailImage}
                          onClick={() =>
                            setModalData({
                              videoSrc: item.video?.[0].videoPreviewURLs[0],
                              isYouTube: false,
                            })
                          }
                          style={{ cursor: "pointer" }}
                        />
                        <button
                          className={styles.playButtonOverlay}
                          onClick={() =>
                            setModalData({
                              videoSrc: item.video?.[0].videoPreviewURLs[0],
                              isYouTube: false,
                            })
                          }
                          aria-label="Play video"
                        >
                          <PlayButtonWhiteBackground />
                        </button>
                      </>
                    ) : item.youtubeVideoId ? (
                      <>
                        <Image
                          src={item.featuredImage?.[0]?.src || "/default-thumbnail.jpg"}
                          width={302}
                          height={182}
                          alt={item.heading}
                          className={styles.thumbnailImage}
                          onClick={() =>
                            setModalData({
                              youtubeId: ytId,
                              isYouTube: true,
                            })
                          }
                          style={{ cursor: "pointer" }}
                        />
                        <button
                          className={styles.playButtonOverlay}
                          onClick={() =>
                            setModalData({
                              youtubeId: ytId,
                              isYouTube: true,
                            })
                          }
                          aria-label="Play YouTube Video"
                        >
                          <PlayButtonWhiteBackground />
                        </button>
                      </>
                    ) : (
                      <Image
                        src={item?.imgUrl}
                        width={302}
                        height={182}
                        alt={item?.heading}
                        className={styles.thumbnailImage}
                        style={{ cursor: "default" }}
                      />
                    )}
                  </div>

                  <div className={styles.article_content}>
                    <h2 className={styles.blog_carousel_heading} title={item.heading}>
                      {item?.heading}
                    </h2>
                    <h3
                      className="font-saira text-black text-[13px] line-clamp-2 mt-4 mb-6 overflow-hidden"
                      title={item?.excerpt}
                      ref={addToExcerptRefs}
                      style={{ minHeight: excerptMinHeight }}
                    >
                      {item?.excerpt}
                    </h3>
                    <Link href={item?.readMoreUrl} target="_blank" className={styles.read_more_btn}>
                      {readMore}
                      <ExpandMore24 className={styles.right_arrow_icon} />
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className={styles.carousel_dot_controls}>
        <div className={styles.carousel_dots_row}>
          {slides?.map((_, index) => {
            if (index > slides.length - 4) return;
            return (
              <DotButton
                key={index}
                onClick={() => onDotButtonClick(index)}
                className={`${styles.carousel_dot_item}`.concat(
                  index === selectedIndex ? ` ${styles.carousel_dot_selected}` : ""
                )}
              />
            );
          })}
        </div>
      </div>

      <VideoModal
        isOpen={!!modalData}
        onClose={() => setModalData(null)}
        videoSrc={modalData?.videoSrc}
        youtubeId={modalData?.youtubeId}
        isYouTube={modalData?.isYouTube || false}
      />
    </section>
  );
};

export default EmblaItemsCarousel;
